import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ImageIcon from '@mui/icons-material/Image';

import MainCard from 'components/MainCard';
import PageHeader from 'components/PageHeader';
import ThemeSwitch from 'components/form/ThemeSwitch';
import useFormValidation from 'hooks/useFormValidation';
import { validators } from 'utils/formValidation';
import api, { resolveAssetUrl } from 'utils/api';
import { useToast } from 'contexts/ToastContext';

export default function ProductSubCategoryForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const location = useLocation();
  const { showToast, showApiError } = useToast();

  const queryParams = new URLSearchParams(location.search);
  const initialCategoryId = queryParams.get('category_id') || '';

  // Form state
  const [form, setForm] = useState({
    name_en: '',
    name_ar: '',
    product_category_id: initialCategoryId,
    sequence: 1,
    status: true,
    remove_bg: false,
    image: null
  });

  const [preview, setPreview] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const {
    formError,
    validate,
    setServerError,
    clearFieldError,
    fieldErrorProps
  } = useFormValidation();

  // Load Parent Categories
  const fetchCategories = useCallback(async () => {
    setLoadingCategories(true);
    try {
      const res = await api.get('/product/category/list');
      const items = Array.isArray(res.data?.data) ? res.data.data : [];
      setCategories(items);
    } catch (err) {
      console.error('Failed to load categories:', err);
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  }, []);

  // Load Subcategory Detail in Edit mode
  const fetchSubCategory = useCallback(async () => {
    if (!id) return;
    setLoadingDetail(true);
    try {
      const res = await api.get(`/product/sub/category/${id}`);
      const data = res.data?.data;
      if (!data) return;

      const catId = String(data.product_category_id || data.category?.id || '');

      setForm({
        name_en: data.name_en || '',
        name_ar: data.name_ar || '',
        product_category_id: catId,
        sequence: data.sequence !== undefined && data.sequence !== null ? data.sequence : 1,
        status: data.status !== undefined ? Boolean(data.status) : true,
        remove_bg: false,
        image: null
      });

      const rawImg =
        data.imageUrl ||
        data.image_url ||
        data.imageDoc?.imageUrl ||
        data.imageDoc?.attachment_url ||
        data.image;

      if (rawImg) {
        const resolved = resolveAssetUrl(rawImg);
        setExistingImageUrl(resolved);
        setPreview(resolved);
      }
    } catch (err) {
      showApiError(err, 'Failed to fetch sub-category details');
    } finally {
      setLoadingDetail(false);
    }
  }, [id, showApiError]);

  useEffect(() => {
    fetchCategories();
    if (isEdit) {
      fetchSubCategory();
    }
  }, [fetchCategories, fetchSubCategory, isEdit]);

  // Clean up object URL on unmount or when preview changes
  useEffect(() => {
    return () => {
      if (preview && preview.startsWith('blob:')) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name) clearFieldError(name);
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (preview && preview.startsWith('blob:')) {
      URL.revokeObjectURL(preview);
    }

    setForm((prev) => ({ ...prev, image: file }));
    setPreview(URL.createObjectURL(file));
  };

  const handleClearSelectedImage = () => {
    if (preview && preview.startsWith('blob:')) {
      URL.revokeObjectURL(preview);
    }
    setForm((prev) => ({ ...prev, image: null }));
    setPreview(existingImageUrl || null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (categories.length === 0 && !loadingCategories) {
      setServerError('A parent category must be created before a sub-category can be added.');
      return;
    }

    const validationSchema = {
      product_category_id: [validators.required('Parent Category')],
      name_en: [
        validators.required('English Name'),
        (val) =>
          val && String(val).length > 150
            ? 'English Name cannot exceed 150 characters'
            : null
      ],
      name_ar: [
        (val) =>
          val && String(val).length > 150
            ? 'Arabic Name cannot exceed 150 characters'
            : null
      ],
      sequence: [
        (val) => {
          if (val === null || val === undefined || val === '') return null;
          const n = Number(val);
          if (Number.isNaN(n) || !Number.isInteger(n) || n < 0) {
            return 'Sequence must be a non-negative integer';
          }
          return null;
        }
      ]
    };

    if (!validate(validationSchema, form)) {
      return;
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('name_en', String(form.name_en).trim());
      if (form.name_ar && String(form.name_ar).trim()) {
        fd.append('name_ar', String(form.name_ar).trim());
      }
      fd.append('product_category_id', String(form.product_category_id).trim());
      fd.append(
        'sequence',
        String(form.sequence !== '' && form.sequence !== null ? form.sequence : 1)
      );
      fd.append('status', form.status ? 'true' : 'false');
      fd.append('remove_bg', form.remove_bg ? 'true' : 'false');

      // Only append image if a new File was selected
      if (form.image instanceof File) {
        fd.append('image', form.image);
      }

      if (isEdit) {
        const res = await api.put(`/product/sub/category/edit/${id}`, fd);
        showToast(res.data?.message || 'Sub-category updated successfully', 'success');
      } else {
        const res = await api.post('/product/sub/category/add', fd);
        showToast(res.data?.message || 'Sub-category created successfully', 'success');
      }

      navigate('/product/sub-category/list');
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to save sub-category';
      setServerError(msg);
      showApiError(err, 'Failed to save sub-category');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      {/* Header */}
      <PageHeader
        variant="nested"
        onBack={() => navigate(-1)}
        backTitle="Back to list"
        title={isEdit ? 'Edit Sub Category' : 'Create Sub Category'}
        subtitle={
          isEdit
            ? 'Update the details and image for this sub-category.'
            : 'Add a new product sub-category linked to a parent category.'
        }
      />

      {/* Form Level Error Alert */}
      {formError ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {formError}
        </Alert>
      ) : null}

      {/* Warning when no categories available */}
      {!loadingCategories && categories.length === 0 ? (
        <Alert severity="warning" sx={{ mb: 3 }}>
          No parent categories found. Please create a parent category first before adding sub-categories.
        </Alert>
      ) : null}

      {loadingDetail ? (
        <MainCard>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={36} />
          </Box>
        </MainCard>
      ) : (
        <Stack spacing={3}>
          <MainCard
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ImageIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Sub Category Details
                </Typography>
              </Box>
            }
          >
            <Grid container spacing={3}>
              {/* Parent Category Select */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  select
                  fullWidth
                  required
                  label="Parent Category"
                  name="product_category_id"
                  value={form.product_category_id}
                  onChange={handleChange}
                  disabled={loadingCategories || categories.length === 0}
                  {...fieldErrorProps('product_category_id')}
                  helperText={
                    fieldErrorProps('product_category_id').helperText ||
                    (loadingCategories ? 'Loading categories…' : undefined)
                  }
                >
                  <MenuItem value="">
                    <em>Select Parent Category</em>
                  </MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.name_en}
                      {cat.name_ar ? ` (${cat.name_ar})` : ''}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Sequence */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Sequence"
                  name="sequence"
                  value={form.sequence}
                  onChange={handleChange}
                  inputProps={{ min: 0, step: 1 }}
                  {...fieldErrorProps('sequence')}
                  helperText={fieldErrorProps('sequence').helperText || 'Display order (default: 1)'}
                />
              </Grid>

              {/* Name (English) */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  label="Name (English)"
                  name="name_en"
                  value={form.name_en}
                  onChange={handleChange}
                  {...fieldErrorProps('name_en')}
                  inputProps={{ maxLength: 150 }}
                />
              </Grid>

              {/* Name (Arabic) */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Name (Arabic)"
                  name="name_ar"
                  value={form.name_ar}
                  onChange={handleChange}
                  {...fieldErrorProps('name_ar')}
                  inputProps={{ maxLength: 150, dir: 'rtl' }}
                />
              </Grid>

              {/* Status Switch */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControlLabel
                  control={
                    <ThemeSwitch
                      checked={form.status}
                      name="status"
                      onChange={handleChange}
                    />
                  }
                  label={
                    <Typography variant="body2" fontWeight={500}>
                      Active Status
                    </Typography>
                  }
                />
              </Grid>

              {/* AI Background Removal */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControlLabel
                  control={
                    <ThemeSwitch
                      checked={form.remove_bg}
                      name="remove_bg"
                      onChange={handleChange}
                    />
                  }
                  label={
                    <Typography variant="body2" fontWeight={500}>
                      Remove Background (AI)
                    </Typography>
                  }
                />
              </Grid>
            </Grid>
          </MainCard>

          {/* Image Upload Card */}
          <MainCard title="Sub Category Image">
            <Stack spacing={2}>
              <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
                {/* Image Preview Box */}
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'grey.50',
                    flexShrink: 0
                  }}
                >
                  {preview ? (
                    <Box
                      component="img"
                      src={preview}
                      alt="Subcategory Preview"
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <ImageIcon sx={{ fontSize: 36, color: 'text.disabled' }} />
                  )}
                </Box>

                {/* Upload & Clear Controls */}
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Button
                      variant="outlined"
                      size="small"
                      component="label"
                      startIcon={<CloudUploadIcon />}
                      sx={{ textTransform: 'none', borderRadius: 2 }}
                    >
                      {form.image || preview ? 'Change Image' : 'Upload Image'}
                      <input
                        type="file"
                        hidden
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        onChange={handleFileChange}
                      />
                    </Button>

                    {form.image ? (
                      <Button
                        variant="text"
                        size="small"
                        color="error"
                        startIcon={<DeleteOutlineIcon />}
                        onClick={handleClearSelectedImage}
                        sx={{ textTransform: 'none' }}
                      >
                        Clear Selected
                      </Button>
                    ) : null}
                  </Stack>

                  <Typography variant="caption" color="text.secondary">
                    {form.image
                      ? `Selected: ${form.image.name} (${(form.image.size / 1024).toFixed(1)} KB)`
                      : isEdit && existingImageUrl
                        ? 'Current image preserved if no new file is uploaded.'
                        : 'Optional. Supported formats: JPG, PNG, WEBP, GIF. Default image assigned if empty.'}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          </MainCard>

          {/* Form Actions */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 1, pb: 4 }}>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              disabled={submitting}
              sx={{ minWidth: 100, borderRadius: 2 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={submitting || (categories.length === 0 && !isEdit)}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ minWidth: 160, borderRadius: 2, fontWeight: 600 }}
            >
              {submitting
                ? 'Saving…'
                : isEdit
                  ? 'Update Sub Category'
                  : 'Create Sub Category'}
            </Button>
          </Box>
        </Stack>
      )}
    </Box>
  );
}
